import { AND } from 'shared/api/query/constants';
import { DateTime } from 'luxon';
import { GOAL_CYCLE } from '@/lib/props/custom-types';
import { UNASSIGNED_VALUE, isDateGrouping } from '@/composables/grid-page/chart/utils';
import { chartGroupByOption, dateScopeDynamicType, dateScopeType, intervalType, propertyType } from 'shared/constants.json';
import { guid } from 'shared/lib/uuid';

const isNumberType = (groupBy) => {
  if (groupBy.isProperty) {
    return groupBy.property?.type === propertyType.number;
  }
  return groupBy.edgeType === propertyType.number;
};

const isDateType = (groupBy) => {
  if (isDateGrouping(groupBy.groupBy)) {
    return true;
  }
  if (groupBy.isProperty) {
    return groupBy.property?.type === propertyType.date;
  }
  return groupBy.edgeType === propertyType.date;
};

const toISOString = (dt) => dt.startOf('second').toISO({ suppressMilliseconds: true });

const buildTimeRange = (groupByOption, originalValue) => {
  let start;
  let end;

  switch (groupByOption) {
    case chartGroupByOption.day: {
      const dt = DateTime.fromISO(originalValue);
      start = toISOString(dt.startOf('day'));
      end = toISOString(dt.endOf('day'));
      break;
    }
    case chartGroupByOption.week: {
      // originalValue is like "2024-W03"
      const dt = DateTime.fromISO(originalValue);
      start = toISOString(dt.startOf('week'));
      end = toISOString(dt.endOf('week'));
      break;
    }
    case chartGroupByOption.month: {
      // originalValue is like "2024-01"
      const dt = DateTime.fromISO(`${originalValue}-01`);
      start = toISOString(dt.startOf('month'));
      end = toISOString(dt.endOf('month'));
      break;
    }
    case chartGroupByOption.quarter: {
      // originalValue is like "2024-Q1"
      const [year, q] = originalValue.split('-Q');
      const month = (parseInt(q, 10) - 1) * 3 + 1;
      const dt = DateTime.fromObject({ year: parseInt(year, 10), month });
      start = toISOString(dt.startOf('quarter'));
      end = toISOString(dt.endOf('quarter'));
      break;
    }
    case chartGroupByOption.year: {
      // originalValue is a number like 2024
      const dt = DateTime.fromObject({ year: originalValue });
      start = toISOString(dt.startOf('year'));
      end = toISOString(dt.endOf('year'));
      break;
    }
    default:
      return null;
  }

  return {
    type: dateScopeType.between,
    dynamicType: dateScopeDynamicType.exactDate,
    start,
    end,
  };
};

const buildNumberRange = (category) => {
  const { bucketMin, bucketMax } = category;

  // Intervals mirror findBucket in custom-buckets.js: [min, max) for ranges,
  // (-∞, max) for "< max", [min, ∞) for "> min".
  if (bucketMin !== undefined || bucketMax !== undefined) {
    if (bucketMin === undefined) {
      return { maxType: intervalType.lt, max: bucketMax };
    }
    if (bucketMax === undefined) {
      return { minType: intervalType.ge, min: bucketMin };
    }
    return { minType: intervalType.ge, min: bucketMin, maxType: intervalType.lt, max: bucketMax };
  }

  const value = category.originalValue;
  return { minType: intervalType.eq, min: value, maxType: intervalType.eq, max: value };
};

const buildPropertyScope = (groupBy, originalValue, category) => {
  const { property } = groupBy;
  const base = { property };

  if (originalValue === UNASSIGNED_VALUE) {
    if (property.type === propertyType.number) {
      return { ...base, isEmpty: true, numberRange: { minType: intervalType.eq, min: 0, maxType: intervalType.eq, max: 0 } };
    }
    if (property.type === propertyType.date) {
      return { ...base, isEmpty: true, timeRange: { type: dateScopeType.empty, dynamicType: dateScopeDynamicType.exactDate } };
    }
    switch (property.type) {
      case propertyType.space:
        return { ...base, isEmpty: true, spaces: [] };
      case propertyType.user:
        return { ...base, isEmpty: true, users: [] };
      case propertyType.options:
      case propertyType.singleSelect:
      case propertyType.status:
      default:
        return { ...base, isEmpty: true, selectedOptions: [] };
    }
  }

  if (property.type === propertyType.number) {
    return { ...base, isEmpty: false, numberRange: buildNumberRange(category) };
  }

  if (property.type === propertyType.date) {
    const timeRange = buildTimeRange(groupBy.groupBy, originalValue);
    return { ...base, timeRange };
  }

  switch (property.type) {
    case propertyType.space:
      return { ...base, spaces: [{ uid: originalValue }] };
    case propertyType.user:
      return { ...base, users: [{ uid: originalValue }] };
    case propertyType.options:
    case propertyType.singleSelect:
    case propertyType.status:
    default:
      return { ...base, selectedOptions: [{ uid: originalValue }] };
  }
};

const buildEdgeScope = (groupBy, originalValue, category) => {
  if (isDateType(groupBy)) {
    if (originalValue === UNASSIGNED_VALUE) {
      return {
        directProperty: {
          edgeName: groupBy.edge,
          type: propertyType.date,
        },
        isEmpty: true,
        timeRange: { type: dateScopeType.empty, dynamicType: dateScopeDynamicType.exactDate },
      };
    }
    const timeRange = buildTimeRange(groupBy.groupBy, originalValue);
    return {
      directProperty: {
        edgeName: groupBy.edge,
        type: propertyType.date,
      },
      timeRange,
    };
  }

  if (isNumberType(groupBy)) {
    if (originalValue === UNASSIGNED_VALUE) {
      return {
        directProperty: {
          edgeName: groupBy.edge,
          type: propertyType.number,
        },
        isEmpty: true,
        numberRange: { minType: intervalType.eq, min: 0, maxType: intervalType.eq, max: 0 },
      };
    }
    return {
      directProperty: {
        edgeName: groupBy.edge,
        type: propertyType.number,
      },
      isEmpty: false,
      numberRange: buildNumberRange(category),
    };
  }

  if (originalValue === UNASSIGNED_VALUE) {
    return {
      directProperty: {
        edgeName: groupBy.edge,
        type: GOAL_CYCLE,
      },
      isEmpty: true,
      goalCycles: [],
    };
  }

  return {
    directProperty: {
      edgeName: groupBy.edge,
      type: GOAL_CYCLE,
    },
    goalCycles: [{ uid: originalValue }],
  };
};

// A single scope containing all cycles so the translator emits one
// uid_in(goalCycle, [...uids]) — i.e. OR across cycles. Emitting one scope per cycle
// would AND them, producing an empty result set since a goal has a single cycle.
const buildGoalCyclesChild = (goalCycles, goalCycleEdge) => ({
  key: guid(),
  op: AND,
  children: [],
  scope: {
    directProperty: {
      edgeName: goalCycleEdge,
      type: GOAL_CYCLE,
    },
    goalCycles: goalCycles.map((cycle) => ({ uid: cycle.uid })),
  },
});

export const buildChartPointFilter = ({ chart, accountUid, category, chartGoalCycles }) => {
  const { groupBy } = chart;
  const categoryOriginalValue = category?.originalValue;

  const baseFilter = chart.filter ? [chart.filter] : [];

  let scopeChild = null;

  if (groupBy.isProperty && groupBy.property?.type) {
    scopeChild = {
      key: guid(),
      op: AND,
      children: [],
      scope: buildPropertyScope(groupBy, categoryOriginalValue, category),
    };
  } else if (groupBy.edge) {
    scopeChild = {
      key: guid(),
      op: AND,
      children: [],
      scope: buildEdgeScope(groupBy, categoryOriginalValue, category),
    };
  }

  const children = [...baseFilter];
  if (scopeChild !== null) {
    children.push(scopeChild);
  }

  if (chartGoalCycles?.length > 0 && groupBy.edge) {
    children.push(buildGoalCyclesChild(chartGoalCycles, groupBy.edge));
  }

  return {
    account: { uid: accountUid },
    op: AND,
    children,
  };
};
